import { useState } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiSend, errorMessage } from "./api";
import InvoicesPanel from "./InvoicesPanel";
import "./MyAccount.css";

const PLAN_INFO = {
  Default: { label: "Default", desc: "Les fonctions essentielles de votre programme de fidélité" },
  Premium: { label: "Premium", desc: "Personnalisation des cartes et statistiques incluses" },
  Extra: { label: "Extra", desc: "L'offre la plus complète" },
  None: { label: "Aucun plan actif", desc: "Souscrivez une offre pour activer toutes les fonctionnalités" },
};

function MyAccount({ user, onNavigate, onLogout, onUserUpdate, onUpgrade }) {
  const [form, setForm] = useState({
    name: user.name,
    surname: user.surname,
    email: user.email,
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [orgName, setOrgName] = useState(user.organization || "");
  const [orgError, setOrgError] = useState("");
  const [orgMessage, setOrgMessage] = useState("");
  const [orgSaving, setOrgSaving] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRenameOrg = async () => {
    setOrgError("");
    setOrgMessage("");
    setOrgSaving(true);
    try {
      await apiSend(`/api/organizations/${user.organization_id}`, "PUT",
        { name: orgName.trim() }, "Erreur lors du renommage");
      onUserUpdate({ ...user, organization: orgName.trim() });
      setOrgMessage("Organisation renommée");
    } catch (e) {
      setOrgError(errorMessage(e, "Erreur lors du renommage"));
    }
    setOrgSaving(false);
  };

  const emailChanged = form.email !== user.email;
  const wantsPasswordChange = form.new_password.length > 0;
  const needsCurrentPassword = emailChanged || wantsPasswordChange;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (wantsPasswordChange && form.new_password !== form.confirm_password) {
      setError("La confirmation ne correspond pas au nouveau mot de passe");
      return;
    }
    if (wantsPasswordChange && form.new_password.length < 8) {
      setError("Le nouveau mot de passe doit faire au moins 8 caractères");
      return;
    }
    if (needsCurrentPassword && !form.current_password) {
      setError("Le mot de passe actuel est requis pour ce changement");
      return;
    }

    const body = {
      name: form.name,
      surname: form.surname,
      email: form.email,
    };
    if (needsCurrentPassword) {
      body.current_password = form.current_password;
    }
    if (wantsPasswordChange) {
      body.new_password = form.new_password;
    }

    setSaving(true);
    try {
      const updated = await apiSend("/api/me", "PUT", body, "Erreur lors de la modification");
      onUserUpdate(updated);
      setForm({ ...form, current_password: "", new_password: "", confirm_password: "" });
      setMessage(wantsPasswordChange ? "Profil et mot de passe mis à jour" : "Profil mis à jour");
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la modification"));
    }
    setSaving(false);
  };

  return (
    <div className="acct-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Mon compte"
        onBack={() => onNavigate("home")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />

      <main className="acct-main">
        <div className="acct-panel acct-summary">
          <h2>{user.surname} {user.name}</h2>
          {/* Un sous-compte Staff voit son organisation mais ne la renomme pas */}
          {user.organization_id && user.role === "Staff" ? (
            <div className="acct-facts">
              <span className="acct-fact"><span className="acct-fact-label">Organisation</span>{user.organization}</span>
            </div>
          ) : user.organization_id ? (
            <div className="acct-org">
              <span className="acct-fact-label">Organisation</span>
              <div className="acct-org-row">
                <input
                  value={orgName}
                  onChange={(e) => { setOrgName(e.target.value); setOrgMessage(""); setOrgError(""); }}
                  maxLength={100}
                  aria-label="Nom de l'organisation"
                />
                {orgName.trim() && orgName.trim() !== user.organization && (
                  <button
                    className="rc-btn rc-btn-outline acct-org-save"
                    onClick={handleRenameOrg}
                    disabled={orgSaving}
                  >
                    {orgSaving ? "..." : "Renommer"}
                  </button>
                )}
              </div>
              {orgError && <div className="acct-error acct-org-msg">{orgError}</div>}
              {orgMessage && <div className="acct-success acct-org-msg">{orgMessage}</div>}
            </div>
          ) : (
            <div className="acct-facts">
              <span className="acct-fact"><span className="acct-fact-label">Organisation</span>Aucune</span>
            </div>
          )}

          {/* Plan : concept commerçant. Sans objet pour un commercial ou un
              sous-compte, et trompeur pour un admin qui a déjà accès à tout
              sans souscrire. */}
          {user.role !== "Sale" && user.role !== "Admin" && user.role !== "Staff" && (
            <div className="acct-plan">
              <div className="acct-plan-text">
                <span className="acct-plan-label">Votre plan</span>
                <span className="acct-plan-name">{(PLAN_INFO[user.plan] || PLAN_INFO.None).label}</span>
                <span className="acct-plan-desc">{(PLAN_INFO[user.plan] || PLAN_INFO.None).desc}</span>
              </div>
              {user.plan !== "Extra" && (
                <button className="rc-btn rc-btn-terracotta acct-plan-upgrade" onClick={onUpgrade}>
                  Améliorer le plan
                </button>
              )}
            </div>
          )}
        </div>

        <form className="acct-panel" onSubmit={handleSubmit}>
          {error && <div className="acct-error">{error}</div>}
          {message && <div className="acct-success">{message}</div>}

          <h3>Informations</h3>
          <label>Nom</label>
          <input name="name" value={form.name} onChange={handleChange} required maxLength={50} />
          <label>Prénom</label>
          <input name="surname" value={form.surname} onChange={handleChange} required maxLength={50} />
          <label>Email</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} required />

          <h3>Changer le mot de passe <span className="acct-optional">(optionnel)</span></h3>
          <label>Nouveau mot de passe</label>
          <input
            name="new_password" type="password" value={form.new_password}
            onChange={handleChange} placeholder="8 caractères minimum" autoComplete="new-password"
          />
          <label>Confirmer le nouveau mot de passe</label>
          <input
            name="confirm_password" type="password" value={form.confirm_password}
            onChange={handleChange} autoComplete="new-password"
          />

          {needsCurrentPassword && (
            <div className="acct-current">
              <label>Mot de passe actuel</label>
              <input
                name="current_password" type="password" value={form.current_password}
                onChange={handleChange} autoComplete="current-password"
                placeholder="Requis pour changer l'email ou le mot de passe"
              />
            </div>
          )}

          <button type="submit" className="rc-btn rc-btn-primary acct-submit" disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </form>

        {/* Facturation : le gérant du commerce uniquement — un commercial ou
            un admin n'a pas d'abonnement, un employé n'a pas à voir les montants */}
        {user.role === "User" && user.organization_id && <InvoicesPanel />}
      </main>
    </div>
  );
}

export default MyAccount;
